/**
 * Spelregels voor de feedback van een melder op zijn softwaremelding
 * (besluit Tom 01-10-2026). Pure functies, gespiegeld aan de database-RPC
 * melding_feedback_geven en de poorttrigger bewaak_melding_feedback — de
 * database beslist, dit bepaalt alleen wat de UI aanbiedt en vooraf afvangt.
 */

export type MeldingStatus = 'nieuw' | 'gepland' | 'afgewezen' | 'opgelost';
export type Feedback = 'werkt' | 'werkt_niet' | 'begrepen' | 'niet_begrepen';

export interface MeldingVoorFeedback {
  melder_id: string | null;
  status: string;
  feedback: string | null;
  feedback_opmerking?: string | null;
  /** Storage-paden (bucket documents) van de foto's bij de feedback, max 5. */
  feedback_foto_paths?: string[] | null;
}

/** Maximaal aantal foto's bij een feedback-antwoord (besluit Tom, melding Pieter 3e6d223c). */
export const MAX_FEEDBACK_FOTOS = 5;

/**
 * Foto's aan de selectie toevoegen tot het maximum. Geeft de nieuwe lijst en
 * hoeveel bestanden er buiten de boot vielen (voor de melding aan de melder).
 * Niet-afbeeldingen vallen af.
 */
export function voegFeedbackFotosToe<T extends { type?: string }>(
  bestaand: readonly T[],
  nieuw: readonly T[],
  max = MAX_FEEDBACK_FOTOS,
): { lijst: T[]; geweigerd: number; geenAfbeelding: number } {
  const afbeeldingen = nieuw.filter((f) => !f.type || f.type.startsWith('image/'));
  const geenAfbeelding = nieuw.length - afbeeldingen.length;
  const ruimte = Math.max(0, max - bestaand.length);
  const toegevoegd = afbeeldingen.slice(0, ruimte);
  return {
    lijst: [...bestaand, ...toegevoegd],
    geweigerd: afbeeldingen.length - toegevoegd.length,
    geenAfbeelding,
  };
}

export interface FeedbackOptie {
  waarde: Feedback;
  label: string;
  /** Opmerking verplicht bij deze keuze. */
  opmerkingVerplicht: boolean;
  /** Heropent de melding (status terug naar nieuw). */
  heropent: boolean;
}

/** Statussen waarbij de melder om feedback gevraagd wordt. */
export const FEEDBACK_STATUSSEN: readonly string[] = ['opgelost', 'afgewezen'];

/** De vraag die de melder te zien krijgt, per status. */
export function feedbackVraag(status: string): string | null {
  if (status === 'opgelost') return 'Werkt het nu?';
  if (status === 'afgewezen') return 'Begrijp je de reden?';
  return null;
}

/** De keuzes die bij een status horen (leeg als er niets te vragen is). */
export function feedbackOpties(status: string): FeedbackOptie[] {
  if (status === 'opgelost') {
    return [
      { waarde: 'werkt', label: 'Ja, werkt', opmerkingVerplicht: false, heropent: false },
      { waarde: 'werkt_niet', label: 'Nee, nog niet', opmerkingVerplicht: true, heropent: true },
    ];
  }
  if (status === 'afgewezen') {
    return [
      { waarde: 'begrepen', label: 'Ja, begrepen', opmerkingVerplicht: false, heropent: false },
      { waarde: 'niet_begrepen', label: 'Nee', opmerkingVerplicht: true, heropent: false },
    ];
  }
  return [];
}

/** Of deze gebruiker nu feedback kan geven op deze melding. */
export function magFeedbackGeven(m: MeldingVoorFeedback, userId: string | undefined): boolean {
  return (
    !!userId &&
    m.melder_id === userId &&
    FEEDBACK_STATUSSEN.includes(m.status) &&
    m.feedback == null
  );
}

/** Wacht op feedback: beoordeeld, melder heeft nog niets teruggemeld. */
export function wachtOpFeedback(m: MeldingVoorFeedback): boolean {
  return FEEDBACK_STATUSSEN.includes(m.status) && m.feedback == null;
}

/**
 * Controle vóór het versturen. Geeft de foutmelding voor de gebruiker, of
 * null als het mag. Dezelfde regels dwingt de database af.
 */
export function valideerFeedback(
  status: string,
  feedback: Feedback | null,
  opmerking: string,
): string | null {
  if (!feedback) return 'Kies een antwoord.';
  const optie = feedbackOpties(status).find((o) => o.waarde === feedback);
  if (!optie) return 'Dit antwoord past niet bij de status van de melding.';
  if (optie.opmerkingVerplicht && opmerking.trim() === '') {
    return status === 'opgelost'
      ? 'Licht kort toe wat er nog niet werkt.'
      : 'Licht kort toe wat je niet begrijpt.';
  }
  return null;
}

export type FeedbackBadgeVariant = 'success' | 'danger' | 'warning' | 'muted';

/**
 * Badge voor Tom en de meekijker: wat heeft de melder teruggemeld?
 * Null = niet van toepassing (melding nog niet beoordeeld).
 */
export function feedbackBadge(m: MeldingVoorFeedback): {
  label: string;
  variant: FeedbackBadgeVariant;
} | null {
  switch (m.feedback) {
    case 'werkt':
      return { label: 'Werkt ✓', variant: 'success' };
    case 'werkt_niet':
      return { label: 'Werkt niet ✗ — heropend', variant: 'danger' };
    case 'begrepen':
      return { label: 'Reden begrepen ✓', variant: 'success' };
    case 'niet_begrepen':
      return { label: 'Reden niet begrepen ✗', variant: 'warning' };
    default:
      return wachtOpFeedback(m) ? { label: 'Nog geen feedback', variant: 'muted' } : null;
  }
}

/** Teller voor de kop van de softwarelijst. */
export function telWachtOpFeedback(meldingen: readonly MeldingVoorFeedback[]): number {
  return meldingen.filter(wachtOpFeedback).length;
}
